import type { HttpTypes } from '@medusajs/types';

import type { OrderDetailData } from '@modules/order/components/order-details/types';

/**
 * 將 order 資料映射為 StoreCart-like 結構，供 CheckoutCart / CartCollapseContainer 等元件使用。
 *
 * Medusa embedded cart 是快照，不含 computed fields（total, subtotal 等），
 * 也不含 item-level 的 variant.calculated_price。
 * 因此改用 order 本身的金額與 enriched items 組合出 cart-compatible 物件。
 *
 * 使用場景：order-completed-template（訂單確認頁）、order-details-template（訂單詳情頁）
 */
export const buildCartFromOrder = (
  order: OrderDetailData,
): HttpTypes.StoreCart | null => {
  if (!order.items?.length) return null;

  const items = order.items.map((item) => ({
    ...item,
    // 將 order item 的 unit_price 合成為 variant.calculated_price，
    // 讓 getPricesForVariant / LineItemPrice 等元件可正確讀取價格
    variant: item.variant
      ? {
          ...item.variant,
          calculated_price: {
            calculated_amount: item.unit_price,
            original_amount: item.unit_price,
            currency_code: order.currency_code,
          },
        }
      : item.variant,
  }));

  // StoreOrderLineItem 與 StoreCartLineItem 結構相近但不完全相容（缺 cart / cart_id），
  // 需 double assertion；cart-level 金額從 order 取得而非 embedded cart
  return {
    ...(order.cart ?? {}),
    items,
    total: order.total,
    subtotal: order.subtotal,
    item_total: order.item_total,
    shipping_total: order.shipping_total,
    discount_total: order.discount_total,
    currency_code: order.currency_code,
  } as unknown as HttpTypes.StoreCart;
};
